// src/pages/Venta.jsx
import React, { useEffect, useState } from 'react';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import ConfirmacionVenta from '../components/ConfirmacionVenta';
import { useUser } from '../context/UserContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

function Venta() {
  const [productos, setProductos] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [carrito, setCarrito] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [clienteSeleccionado, setClienteSeleccionado] = useState('');
  const [clienteData, setClienteData] = useState(null);
  const [warning, setWarning] = useState('');
  const [mostrarModal, setMostrarModal] = useState(false);
  const [metodoPago, setMetodoPago] = useState('efectivo');
  const [moneda, setMoneda] = useState('USD');
  const [vendedor, setVendedor] = useState('Feiber');
  const [esCredito, setEsCredito] = useState(false);
  const { tasaBCV } = useUser();

  // Para PDF
  const [mostrarPDF, setMostrarPDF] = useState(false);
  const [datosFactura, setDatosFactura] = useState(null);

  // ============================
  // CARGA INICIAL DE DATOS + servicio_pendiente
  // ============================
  useEffect(() => {
    const obtenerProductos = async () => {
      const snap = await getDocs(collection(db, 'inventario'));
      const productosData = snap.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        precio: parseFloat(doc.data().precio) || 0
      }));
      setProductos(productosData);
    };

    const obtenerClientes = async () => {
      const snap = await getDocs(collection(db, 'clientes'));
      const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setClientes(data);
    };

    obtenerProductos();
    obtenerClientes();

    // Si llegas desde Servicios1.jsx (servicio pendiente)
    const servicioPendiente = localStorage.getItem("servicio_pendiente");
    if (servicioPendiente) {
      try {
        const servicio = JSON.parse(servicioPendiente);
        setCarrito(prev => [
          ...prev,
          {
            ...servicio,
            id: 'servicio',
            esServicio: true
          }
        ]);
        if (servicio.clienteId) setClienteSeleccionado(servicio.clienteId);
      } catch (e) { /* ignora error de parse */ }
      localStorage.removeItem("servicio_pendiente");
    }

    window.addEventListener('online', sincronizarVentasPendientes);
    return () => window.removeEventListener('online', sincronizarVentasPendientes);
  }, []);

  // ============================
  // Actualizar info del cliente al cambiar select
  // ============================
  useEffect(() => {
    const seleccionado = clientes.find(c => c.id === clienteSeleccionado);
    setClienteData(seleccionado || null);
  }, [clienteSeleccionado, clientes]);

  // ============================
  // CARRITO
  // ============================
  const agregarAlCarrito = (producto) => {
    if (producto.cantidad === 0) {
      setWarning(`No hay stock disponible para ${producto.nombre}`);
      return;
    }
    const existente = carrito.find(item => item.id === producto.id);
    if (existente) {
      setCarrito(prev =>
        prev.map(item =>
          item.id === producto.id
            ? { ...item, cantidad: item.cantidad + 1 }
            : item
        )
      );
    } else {
      setCarrito(prev => [...prev, { ...producto, cantidad: 1 }]);
    }
    setWarning('');
  };

  const eliminarDelCarrito = (id) => {
    setCarrito(prev => prev.filter(item => item.id !== id));
  };

  const actualizarCantidad = (id, cantidad) => {
    setCarrito(prev =>
      prev.map(item =>
        item.id === id ? { ...item, cantidad: Number(cantidad) } : item
      )
    );
  };

  // ============================
  // CALCULOS
  // ============================
  const subTotalConIVA = carrito.reduce(
    (acc, item) => acc + item.precio * item.cantidad, 0);
  const neto = subTotalConIVA / 1.16;
  const IVACompleto = subTotalConIVA - neto;
  let retencionIVA = 0;
  if (clienteData?.tipo === 'juridico' && clienteData?.porcentajeRetencion) {
    const factor = clienteData.porcentajeRetencion / 100;
    retencionIVA = IVACompleto * factor;
  }
  const total = subTotalConIVA - retencionIVA;

  // ============================
  // FILTRO DE PRODUCTOS
  // ============================
  const productosFiltrados = busqueda
    ? productos.filter(
        p =>
          p.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
          p.codigo?.toLowerCase().includes(busqueda.toLowerCase())
      )
    : [];

  // ============================
  // SINCRONIZACION OFFLINE
  // ============================
  const sincronizarVentasPendientes = async () => {
    const ventasPendientes = JSON.parse(localStorage.getItem('ventas_pendientes')) || [];
    for (const venta of ventasPendientes) {
      await addDoc(collection(db, 'ventas'), venta);
    }
    localStorage.removeItem('ventas_pendientes');
    alert('Ventas pendientes sincronizadas');
  };

  // ============================
  // DESCUENTO DE INVENTARIO
  // ============================
  const descontarInventario = async () => {
    for (const item of carrito) {
      if (item.esServicio) continue; // no descuenta inventario si es un servicio
      const productoRef = doc(db, 'inventario', item.id);
      const stockActual = productos.find(p => p.id === item.id)?.cantidad || 0;
      const nuevoStock = stockActual - item.cantidad;
      if (nuevoStock >= 0) {
        await updateDoc(productoRef, { cantidad: nuevoStock });
      }
    }
  };

  // ============================
  // CONFIRMAR VENTA
  // ============================
  const confirmarVenta = async () => {
    if (!carrito.length) {
      alert('Carrito vacío. No se puede confirmar.');
      return;
    }
    if (esCredito && !clienteSeleccionado) {
      alert('Debes seleccionar un cliente para venta a crédito.');
      return;
    }
    const nuevaVenta = {
      clienteId: clienteSeleccionado || 'no-aplica',
      productos: carrito,
      subTotalConIVA,
      neto,
      IVACompleto,
      retencionIVA,
      total,
      fecha: Timestamp.now(),
      metodoPago,
      moneda,
      vendedor,
      montoBS: moneda === 'BS' ? total : total * tasaBCV,
      montoUSD: moneda === 'USD' ? total : total / tasaBCV
    };

    try {
      await descontarInventario();

      if (navigator.onLine) {
        if (esCredito) {
          await addDoc(collection(db, 'facturasPorCobrar'), {
            ...nuevaVenta,
            estado: 'pendiente'
          });
          alert('Factura a crédito creada. Se descuenta inventario, pero no se registra en ventas todavía.');
        } else {
          await addDoc(collection(db, 'ventas'), nuevaVenta);
          if (retencionIVA > 0) {
            await addDoc(collection(db, 'retenciones'), {
              tipo: 'venta',
              cliente: clienteData?.nombre,
              rif: clienteData?.rif || '',
              montoConIVA: subTotalConIVA,
              neto,
              iva: IVACompleto,
              retencion: retencionIVA,
              porcentajeRetencion: clienteData?.porcentajeRetencion,
              total,
              fecha: Timestamp.now()
            });
          }
          // Lanza modal para PDF
          setDatosFactura({
            venta: nuevaVenta,
            cliente: clienteData,
            productos: carrito,
            vendedor,
            subTotalConIVA,
            neto,
            IVACompleto,
            retencionIVA,
            total
          });
          setMostrarPDF(true);
        }
      } else {
        const pendientes = JSON.parse(localStorage.getItem('ventas_pendientes')) || [];
        pendientes.push(nuevaVenta);
        localStorage.setItem('ventas_pendientes', JSON.stringify(pendientes));
        alert('Sin conexión. La venta se guardó localmente.');
      }

      setMostrarModal(false);
      // No limpiar el carrito aquí, solo después de imprimir

    } catch (error) {
      console.error('Error registrando la venta:', error);
      alert('Error registrando la venta. Intenta nuevamente.');
    }
  };

  // ============================
  // PDF
  // ============================
  const imprimirFactura = () => {
    if (!datosFactura) return;
    const { venta, cliente, productos, vendedor, subTotalConIVA, neto, IVACompleto, retencionIVA, total } = datosFactura;
    const docPDF = new jsPDF();

    docPDF.setFontSize(16);
    docPDF.text('FRENOS & MULTISERVICIOS GLOBAL', 15, 15);
    docPDF.setFontSize(12);
    docPDF.text(`Factura de Venta`, 15, 25);
    docPDF.text(`Fecha: ${new Date().toLocaleString()}`, 15, 33);
    docPDF.text(`Cliente: ${cliente?.nombre || '---'}`, 15, 41);
    if (cliente?.cedula) docPDF.text(`Cédula: ${cliente.cedula}`, 15, 49);
    if (cliente?.rif) docPDF.text(`RIF: ${cliente.rif}`, 15, 57);
    docPDF.text(`Vendedor: ${vendedor || '---'}`, 15, 65);

    autoTable(docPDF, {
      startY: 73,
      head: [['Producto', 'Cantidad', 'Precio', 'Subtotal']],
      body: productos.map((p) => [
        p.nombre,
        p.cantidad,
        `$${p.precio.toFixed(2)}`,
        `$${(p.precio * p.cantidad).toFixed(2)}`
      ]),
      theme: 'grid'
    });

    let yFinal = docPDF.lastAutoTable ? docPDF.lastAutoTable.finalY + 6 : 100;
    docPDF.text(`Subtotal: $${subTotalConIVA?.toFixed(2) || '0.00'}`, 15, yFinal);
    docPDF.text(`Neto s/IVA: $${neto?.toFixed(2) || '0.00'}`, 15, yFinal + 7);
    docPDF.text(`IVA (16%): $${IVACompleto?.toFixed(2) || '0.00'}`, 15, yFinal + 14);
    if (retencionIVA > 0) docPDF.text(`Retención: -$${retencionIVA?.toFixed(2) || '0.00'}`, 15, yFinal + 21);
    docPDF.text(`Total: $${total?.toFixed(2) || '0.00'}`, 15, yFinal + 28);

    docPDF.save(`factura_${cliente?.nombre || 'venta'}.pdf`);
    setMostrarPDF(false);
    setDatosFactura(null);
    setCarrito([]); // Limpiar carrito al cerrar PDF
  };

  // ============================
  // RENDER
  // ============================
  return (
    <div className="venta-container">
      <div className="bcv-tasa-flotante">
        Tasa BCV: <strong>{tasaBCV} Bs/USD</strong>
      </div>

      {/* BUSCADOR */}
      <div className="venta-busqueda">
        <input
          type="text"
          placeholder="Buscar por nombre o código..."
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
        />
      </div>

      {/* SELECCION CLIENTE / CREDITO */}
      <div className="venta-clientes">
        <label>Venta a crédito:</label>
        <input
          type="checkbox"
          checked={esCredito}
          onChange={e => setEsCredito(e.target.checked)}
          style={{ marginLeft: '8px' }}
        />

        <br /><br />
        <label>Seleccionar cliente:</label>
        <select
          value={clienteSeleccionado}
          onChange={e => setClienteSeleccionado(e.target.value)}
        >
          <option value="">-- Opcional --</option>
          {clientes.map(cliente => (
            <option key={cliente.id} value={cliente.id}>
              {cliente.nombre}
            </option>
          ))}
        </select>

        {/* Retención */}
        {clienteData?.tipo === 'juridico' && (
          <p style={{ color: 'red' }}>
            Cliente jurídico, retención IVA ({clienteData?.porcentajeRetencion || 100}%)
          </p>
        )}
      </div>

      {warning && <div className="warning">{warning}</div>}

      {/* RESULTADOS DE BUSQUEDA */}
      <div className="venta-resultados">
        {productosFiltrados.map(producto => (
          <div key={producto.id} className="producto-card">
            <h4>{producto.nombre}</h4>
            <p><strong>Código:</strong> {producto.codigo}</p>
            <p><strong>Precio (c/IVA):</strong> ${producto.precio.toFixed(2)}</p>
            <p><strong>Stock:</strong> {producto.cantidad}</p>
            <button onClick={() => agregarAlCarrito(producto)}>Agregar</button>
          </div>
        ))}
      </div>

      {/* CARRITO */}
      <div className="carrito">
        <h3>Carrito</h3>
        {carrito.map((item, index) => (
          <div key={index} className="carrito-item">
            <span>{item.nombre}</span>
            <input
              type="number"
              min="1"
              value={item.cantidad}
              onChange={e => actualizarCantidad(item.id, e.target.value)}
              className="cantidad-input"
            />
            <span>${(item.precio * item.cantidad).toFixed(2)}</span>
            <button onClick={() => eliminarDelCarrito(item.id)}>
              Eliminar
            </button>
          </div>
        ))}

        {/* DESGLOSE DE MONTOS */}
        <div className="carrito-total">
          <p>Subtotal c/IVA: ${subTotalConIVA.toFixed(2)}</p>
          <p>Neto s/IVA: ${neto.toFixed(2)}</p>
          <p>IVA (16%): ${IVACompleto.toFixed(2)}</p>
          {retencionIVA > 0 && (
            <p>
              Retención ({clienteData?.porcentajeRetencion}%) = -$
              {retencionIVA.toFixed(2)}
            </p>
          )}
          <p>
            <strong>Total: ${total.toFixed(2)}</strong>
          </p>
        </div>

        {/* DATOS DE VENDEDOR Y PAGO */}
        <select
          className="vendedor-select"
          value={vendedor}
          onChange={e => setVendedor(e.target.value)}
        >
          <option value="Feiber">Vendedor: Feiber</option>
          <option value="Daniel">Vendedor: Daniel</option>
        </select>

        <div style={{ marginTop: '10px' }}>
          <label>Pago:</label>
          <select value={metodoPago} onChange={(e) => setMetodoPago(e.target.value)}>
            <option value="efectivo">Efectivo</option>
            <option value="tarjeta">Tarjeta</option>
          </select>

          <label style={{ marginLeft: '10px' }}>Moneda:</label>
          <select value={moneda} onChange={(e) => setMoneda(e.target.value)}>
            <option value="USD">$ USD</option>
            <option value="BS">Bs (BCV)</option>
          </select>
        </div>

        {/* BOTON CONFIRMAR */}
        <button
          className="btn-registrar"
          onClick={() => setMostrarModal(true)}
          disabled={carrito.length === 0}
        >
          Confirmar venta
        </button>
      </div>

      {/* MODAL CONFIRMAR */}
      {mostrarModal && (
        <ConfirmacionVenta
          carrito={carrito}
          total={total}
          metodoPago={metodoPago}
          moneda={moneda}
          tasaBCV={tasaBCV}
          onConfirm={confirmarVenta}
          onCancel={() => setMostrarModal(false)}
        />
      )}

      {/* MODAL PDF */}
      {mostrarPDF && (
        <div className="modal-pdf">
          <div className="modal-pdf-content">
            <h3>¡Venta confirmada!</h3>
            <p>Puedes imprimir la factura para el cliente.</p>
            <button onClick={imprimirFactura}>Imprimir Factura (PDF)</button>
            <button
              style={{ marginLeft: 10 }}
              onClick={() => {
                setMostrarPDF(false);
                setDatosFactura(null);
                setCarrito([]);
              }}
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Venta;
